import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaSiete(props, ref) {
  const { content, logoSrc, bgColor } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) return resolve();
      const containerWidth = wrapperRef.current ? wrapperRef.current.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      // landscape Letter ratio
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      const padding = typeof opts.padding === 'number' ? opts.padding : 48;
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#111827';

      // title
      const titleFontSize = opts.titleFontSize || 45;
      ctx.textBaseline = 'top';
      const title = (opts.title || props.title) || 'Desplazamiento Forzado Intraurbano';
      let y = padding;
      if (title) {
        // badge: filled square with yellow fill and yellow/orange border and white number
        // make badge twice bigger
        const badgeSize = Math.round(titleFontSize * 2);
        const badgeX = padding;
        const badgeY = y;

        // filled badge (yellow) and contrasting border (darker yellow/orange)
        ctx.fillStyle = opts.badgeFillColor || '#1578faff'; // yellow fill
        ctx.fillRect(badgeX, badgeY, badgeSize, badgeSize);

        const strokeW = Math.max(2, Math.round(badgeSize * 0.06));
        ctx.lineWidth = strokeW;
        ctx.strokeStyle = opts.badgeBorderColor || '#1578faff'; // darker border
        ctx.strokeRect(badgeX + strokeW / 2, badgeY + strokeW / 2, badgeSize - strokeW, badgeSize - strokeW);

        // white number centered in the badge
        ctx.fillStyle = opts.badgeNumberColor || '#ffffff';
        ctx.font = `bold ${Math.round(badgeSize * 0.55)}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('2', badgeX + badgeSize / 2, badgeY + badgeSize / 2);

        // draw title text to the right of badge — vertically centered to the badge
        const titleX = badgeX + badgeSize + 24;
        ctx.fillStyle = '#111827';
        ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'start';
        ctx.textBaseline = 'top';
        // compute title Y so title is vertically centered relative to the badge
        const titleY = badgeY + Math.round((badgeSize - titleFontSize) / 2);
        ctx.fillText(title, titleX, titleY);

        // force content start below badge
        // add 200px vertical gap before body cards
        y = badgeY + badgeSize + 150;
      }

      // normalize content to array-of-arrays
      const rawBody = props.content !== undefined ? props.content : (content !== undefined ? content : []);
      const body = Array.isArray(rawBody) ? rawBody : [rawBody];

      // icons provided per card: prefer props.cardIcons (array of src) then opts.cardIcons
      // example: props.cardIcons = ['/img/c0.png','/img/c1.png','/img/c2.png','/img/c3.png','/img/c4.png','/img/c5.png']
      const cardIcons = (props.cardIcons || opts.cardIcons) || [];
      const iconLoadPromises = [];

      // draw cards explicitly (no for loop) — 2 columns layout (fixed positions)
      const perRow = 2;
      const gap = typeof opts.cardsGap === 'number' ? opts.cardsGap : 24;
      const verticalGap = typeof opts.cardsVGap === 'number' ? opts.cardsVGap : 20;
      const cardsCount = body.length;
      const cardWidth = Math.floor((width - padding * 2 - (perRow - 1) * gap) / perRow);
      const cardHeight = typeof opts.cardHeight === 'number' ? opts.cardHeight : 350;

      // helper: draw a simple family icon (two heads + bodies) at (ix,iy) with given size
      const drawFamilyIcon = (ix, iy, famIconSize, fg = '#ffffff', bg = null) => {
        ctx.save();
        if (bg) {
          ctx.fillStyle = bg;
          ctx.beginPath();
          ctx.arc(ix + famIconSize / 2, iy + famIconSize / 2, famIconSize / 2 + 6, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = fg;
        // left head
        ctx.beginPath();
        ctx.arc(ix + famIconSize * 0.30, iy + famIconSize * 0.36, famIconSize * 0.14, 0, Math.PI * 2);
        ctx.fill();
        // right head
        ctx.beginPath();
        ctx.arc(ix + famIconSize * 0.70, iy + famIconSize * 0.36, famIconSize * 0.14, 0, Math.PI * 2);
        ctx.fill();
        // left body
        ctx.beginPath();
        ctx.ellipse(ix + famIconSize * 0.30, iy + famIconSize * 0.72, famIconSize * 0.20, famIconSize * 0.12, 0, 0, Math.PI * 2);
        ctx.fill();
        // right body
        ctx.beginPath();
        ctx.ellipse(ix + famIconSize * 0.70, iy + famIconSize * 0.72, famIconSize * 0.20, famIconSize * 0.12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      };

      const drawCardAt = (i, col, rowIdx) => {
        if (i >= body.length) return;
        const cardX = padding + col * (cardWidth + gap);
        const cardY = y + rowIdx * (cardHeight + verticalGap);

        // card background/colors
        const defaultCardBgColors = opts.cardBgColors || ['#68dfd5ff', 'rgb(114, 194, 231)'];
        const cardBgColor = defaultCardBgColors[i] || opts.cardBg || '#ffffff';
        ctx.fillStyle = cardBgColor;
        ctx.fillRect(cardX, cardY, cardWidth, cardHeight);
        ctx.lineWidth = 1;
        const defaultBorderColors = opts.cardBorderColors || ['#50b9ffff', '#014261ff'];
        ctx.strokeStyle = defaultBorderColors[i] || opts.cardBorder || '#d1d5db';
        ctx.strokeRect(cardX + 0.5, cardY + 0.5, cardWidth - 1, cardHeight - 1);

        // readable text color
        const hexToLuma = (hex) => {
          const h = (hex || '#ffffff').replace('#', '');
          const r = parseInt(h.substring(0, 2), 16);
          const g = parseInt(h.substring(2, 4), 16);
          const b = parseInt(h.substring(4, 6), 16);
          return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        };
        const useLightText = hexToLuma(cardBgColor) < 0.6;
        const cardValueColor = useLightText ? (props.cardValueLightColor || '#ffffff') : (props.cardValueColor || '#111827');

        // icon at top-left
        const iconPad = 12;
        const famIconSize = 80;
        const ix = cardX + iconPad;
        const iy = cardY + iconPad;
        const iconFg = useLightText ? (opts.familyIconDarkOnLight || '#111827') : (opts.familyIconColor || '#ffffff');
        const iconBg = useLightText ? (opts.familyIconBgLight || null) : (opts.familyIconBg || 'rgba(255,255,255,0.08)');
        const iconSrc = Array.isArray(cardIcons) ? cardIcons[i] : cardIcons;
        // draw gray circle behind every card image/icon for consistent background
        ctx.save();
        ctx.fillStyle = opts.cardIconCircleBg || 'rgba(107,114,128,0.18)'; // subtle gray
        ctx.beginPath();
        ctx.arc(ix + famIconSize / 2, iy + famIconSize / 2, Math.round(famIconSize / 2) + 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        if (iconSrc) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          const p = new Promise((res) => {
            img.onload = () => { try { ctx.drawImage(img, ix, iy, famIconSize, famIconSize); } catch (e) {} ; res(); };
            img.onerror = () => res();
            img.src = iconSrc;
          });
          iconLoadPromises.push(p);
        } else {
          drawFamilyIcon(ix, iy, famIconSize, iconFg, iconBg);
        }

        // values (centered)
        const items = Array.isArray(body[i]) ? body[i] : [];
        const valueFontSize = typeof opts.cardValueSize === 'number' ? opts.cardValueSize : 28;
        const rowGap = typeof opts.cardRowGap === 'number' ? opts.cardRowGap : 12;
        let curY = cardY + 100;
        const centerX = cardX + Math.floor(cardWidth / 2);
        const maxW = cardWidth - 32;

        for (let r = 0; r < items.length; r++) {
          const it = items[r] || {};
          ctx.fillStyle = (r === 0) ? '#ffffff' : cardValueColor;
          ctx.font = `${(r === 2 || r === 3) ? 'bold' : ''} ${(r === 2 || r === 3) ? 28 : valueFontSize}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          const words = String(it.text || '0').split(' ');
          const baseStartY = curY;
          const textCenterX = centerX + (r === 0 ? 40 : 0);
          let line = '';
          let lineCount = 1;
          let ty = baseStartY + (r === 0 ? -40 : 0);
          for (let w = 0; w < words.length; w++) {
            const test = line ? line + ' ' + words[w] : words[w];
            if (ctx.measureText(test).width > maxW && line) {
              ctx.fillText(line, textCenterX, ty);
              line = words[w];
              ty += valueFontSize + 6;
              lineCount += 1;
            } else {
              line = test;
            }
          }
          if (line) ctx.fillText(line, textCenterX, ty);
          curY = baseStartY + (lineCount - 1) * (valueFontSize + 6) + valueFontSize + rowGap;
        }
      };

      // explicit draws (no loop)
      // Top row: two full-width cards (left / right)
      drawCardAt(0, 0, 0);
      drawCardAt(1, 1, 0);

      // Second row: four small cards in one row — two small cards under each of the top cards
      const secondRowY = y + 1 * (cardHeight + verticalGap);
      const leftColX = padding + 0 * (cardWidth + gap);
      const rightColX = padding + 1 * (cardWidth + gap);
      const innerSmallGap = typeof opts.smallCardsGap === 'number' ? opts.smallCardsGap : 12;
      const smallWidth = Math.floor((cardWidth - innerSmallGap) / 2);
      const smallHeight = typeof opts.smallCardHeight === 'number' ? opts.smallCardHeight : cardHeight;

      const drawSmallCardAt = (i, x, yTop, w, h) => {
        if (i >= body.length) return;
        // card background/colors (use same defaults but indexed by i)
        const defaultCardBgColors = opts.cardBgColors || ['#50b9ffff', '#014261ff'];
        // forced colors for specific small-card indices (can be overridden via opts)
        const forcedBgMap = {
          2: opts.cardBgIndex2 || '#a5d5f5ff', // green for card index 2
          3: opts.cardBgIndex3 || '#ff8c42', // orange for card index 3
          4: opts.cardBgIndex4 || '#0e7db1ff', // blue for card index 4
          5: opts.cardBgIndex5 || '#facc15'  // yellow for card index 5
        };
        const cardBgColor = forcedBgMap[i] || defaultCardBgColors[i] || opts.cardBg || '#ffffff';
        ctx.fillStyle = cardBgColor;
        ctx.fillRect(x, yTop, w, h);
        ctx.lineWidth = 1;
        const defaultBorderColors = opts.cardBorderColors || ['#50b9ffff', '#014261ff'];
        ctx.strokeStyle = defaultBorderColors[i] || opts.cardBorder || '#d1d5db';
        ctx.strokeRect(x + 0.5, yTop + 0.5, w - 1, h - 1);

        // readable text color
        const hexToLuma = (hex) => {
          const hh = (hex || '#ffffff').replace('#', '');
          const r = parseInt(hh.substring(0, 2), 16);
          const g = parseInt(hh.substring(2, 4), 16);
          const b = parseInt(hh.substring(4, 6), 16);
          return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        };
        const useLightText = hexToLuma(cardBgColor) < 0.6;
        const cardValueColor = useLightText ? (props.cardValueLightColor || '#111827') : (props.cardValueColor || '#111827');

        // icon at top-left (scale with small width)
        const iconPad = 12;
        const famIconSize = Math.min(80, Math.round(w * 0.35));
        const ix = x + iconPad;
        const iy = yTop + iconPad;
        const iconFg = useLightText ? (opts.familyIconDarkOnLight || '#111827') : (opts.familyIconColor || '#ffffff');
        const iconBg = useLightText ? (opts.familyIconBgLight || null) : (opts.familyIconBg || 'rgba(255,255,255,0.08)');
        const iconSrc = Array.isArray(cardIcons) ? cardIcons[i] : cardIcons;
        // draw gray circle behind every small-card image/icon
        ctx.save();
        ctx.fillStyle = opts.cardIconCircleBg || 'rgba(153, 156, 161, 0.72)';
        ctx.beginPath();
        ctx.arc(ix + famIconSize / 2, iy + famIconSize / 2, Math.round(famIconSize / 2) + 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        if (iconSrc) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          const p = new Promise((res) => {
            img.onload = () => { try { ctx.drawImage(img, ix, iy, famIconSize, famIconSize); } catch (e) {} ; res(); };
            img.onerror = () => res();
            img.src = iconSrc;
          });
          iconLoadPromises.push(p);
        } else {
          drawFamilyIcon(ix, iy, famIconSize, iconFg, iconBg);
        }

        // values (centered) — same rendering as in drawCardAt but constrained to w/h
        const items = Array.isArray(body[i]) ? body[i] : [];
        const valueFontSize = typeof opts.cardValueSize === 'number' ? opts.cardValueSize : 25;
        const rowGap = typeof opts.cardRowGap === 'number' ? opts.cardRowGap : 12;
        let curY = yTop + 100;
        const centerX = x + Math.floor(w / 2);
        const maxW = w - 32;

        for (let r = 0; r < items.length; r++) {
          const it = items[r] || {};
          ctx.fillStyle = (r === 0) ? '#ffffff' : cardValueColor;
          ctx.font = `${(r === 2 || r === 3) ? 'bold' : ''} ${(r === 2 || r === 3) ? 28 : valueFontSize}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          const words = String(it.text || '0').split(' ');
          const baseStartY = curY;
          const textCenterX = centerX + (r === 0 ? 40 : 0);
          let line = '';
          let lineCount = 1;
          let ty = baseStartY + (r === 0 ? -40 : 0);
          for (let wI = 0; wI < words.length; wI++) {
            const test = line ? line + ' ' + words[wI] : words[wI];
            if (ctx.measureText(test).width > maxW && line) {
              ctx.fillText(line, textCenterX, ty);
              line = words[wI];
              ty += valueFontSize + 6;
              lineCount += 1;
            } else {
              line = test;
            }
          }
          if (line) ctx.fillText(line, textCenterX, ty);
          curY = baseStartY + (lineCount - 1) * (valueFontSize + 6) + valueFontSize + rowGap;
        }
      };

      // draw the four small cards in the second row: two under left column and two under right column
      drawSmallCardAt(2, leftColX, secondRowY, smallWidth, smallHeight);
      drawSmallCardAt(3, leftColX + smallWidth + innerSmallGap, secondRowY, smallWidth, smallHeight);
      drawSmallCardAt(4, rightColX, secondRowY, smallWidth, smallHeight);
      drawSmallCardAt(5, rightColX + smallWidth + innerSmallGap, secondRowY, smallWidth, smallHeight);

      // wait for any card icon images to load, then draw logo and resolve
      const logoSrcFinal = opts.logoSrc || logoSrc;
      const logoPromise = logoSrcFinal
        ? new Promise((res) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try {
                const w = Math.min(200, width * 0.18);
                const h = (img.height / img.width) * w;
                const lx = width - padding - w;
                const ly = height - padding - h;
                ctx.drawImage(img, lx, ly, w, h);
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error('GetPaginaTres: drawImage failed', e);
              }
              res();
            };
            img.onerror = () => res();
            img.src = logoSrcFinal;
          })
        : Promise.resolve();

      Promise.all([...iconLoadPromises, logoPromise]).then(() => resolve());
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_tres_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});
